import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Search, ArrowRight, ShieldCheck, Compass } from "lucide-react";

export default function PartnersPage() {
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (city.trim()) params.city = city.trim();
      if (pincode.trim()) params.pincode = pincode.trim();
      const res = await api.get("/partners", { params });
      setPartners(res.data);
    } catch {
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Find a centre</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-10">Certified PUC partners near you</h1>

        <div className="grid md:grid-cols-[1fr_1fr_auto] gap-3 border border-border p-4 bg-card mb-10" data-testid="filter-bar">
          <Input data-testid="filter-city" placeholder="City (e.g. Bengaluru)" value={city} onChange={(e) => setCity(e.target.value)} className="rounded-lg bg-secondary border-border h-11" />
          <Input data-testid="filter-pincode" placeholder="Pincode (e.g. 560001)" value={pincode} onChange={(e) => setPincode(e.target.value)} className="rounded-lg bg-secondary border-border h-11" />
          <Button data-testid="filter-search-btn" onClick={load} className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold h-11 px-6">
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
        </div>

        {loading ? (
          <div className="text-muted-foreground">Loading centres…</div>
        ) : partners.length === 0 ? (
          <div className="border border-dashed border-border p-16 text-center" data-testid="empty-partners">
            <h3 className="font-display text-2xl font-bold mb-2">No centres found</h3>
            <p className="text-muted-foreground">Try a different city or pincode.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border" data-testid="partners-grid">
            {partners.map((p) => (
              <div key={p.partner_id} data-testid={`partner-card-${p.partner_id}`} className="bg-card p-6 flex flex-col group">
                <div className="flex items-start justify-between mb-4">
                  <Badge className="rounded-lg border border-primary/30 bg-primary/10 text-primary flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" /> RTO Certified
                  </Badge>
                </div>
                <h3 className="font-display text-xl font-bold tracking-tight mb-2">{p.store_name}</h3>
                <div className="text-sm text-muted-foreground flex items-start gap-2 mb-4">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{p.address}, {p.city} {p.pincode}, {p.state}</span>
                </div>
                <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1">Vehicles served</div>
                <div className="flex flex-wrap gap-1 mb-6">
                  {(p.services_offered || []).map((s) => (
                    <span key={s} className="text-xs bg-secondary px-2 py-1 border border-border">{s.replace("_", " ")}</span>
                  ))}
                </div>
                <Link to={`/book/${p.partner_id}`} className="mt-auto">
                  <Button data-testid={`book-${p.partner_id}`} className="w-full rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                    Book here <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <Link to={`/partner/${p.partner_id}`} data-testid={`details-${p.partner_id}`}>
                    <Button variant="outline" size="sm" className="w-full rounded-lg border-border text-foreground hover:bg-secondary h-9 text-xs">View details</Button>
                  </Link>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${p.store_name}, ${p.address}, ${p.city} ${p.pincode}`)}`} target="_blank" rel="noopener noreferrer" data-testid={`directions-${p.partner_id}`}>
                    <Button variant="outline" size="sm" className="w-full rounded-lg border-border text-foreground hover:bg-secondary h-9 text-xs"><Compass className="w-3 h-3 mr-1" /> Directions</Button>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
